import React, { useState, useEffect } from 'react';
import { X, ClipboardList, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { supabase } from '../services/supabaseClient';

const SERVICE_TYPES = [
  { value: 'emergency_roadside', label: 'Emergency Roadside' },
  { value: 'towing', label: 'Towing' },
  { value: 'battery', label: 'Battery' },
  { value: 'puncture', label: 'Tyre/Puncture' },
  { value: 'fuel', label: 'Fuel' },
  { value: 'lockout', label: 'Lockout/Key' },
  { value: 'general', label: 'General Inquiry' },
  { value: 'other', label: 'Other' },
];

const DISPOSITIONS = [
  { value: 'completed', label: 'Completed / Service Provided' },
  { value: 'not_linked', label: 'Not Linked (No Complaint)' },
  { value: 'wrong_number', label: 'Wrong Number' },
  { value: 'cancelled', label: 'Cancelled by Customer' },
  { value: 'follow_up', label: 'Follow-up Required' },
  { value: 'no_service_needed', label: 'No Service Needed' },
  { value: 'out_of_area', label: 'Out of Service Area' },
  { value: 'spam', label: 'Spam/Unwanted' },
  { value: 'test_call', label: 'Test Call' },
  { value: 'other', label: 'Other' },
];

// Built-in city suggestions (works even if DB has no cities yet)
const DEFAULT_CITIES = [
  'Ahmedabad',
  'Bengaluru',
  'Bhopal',
  'Chandigarh',
  'Chennai',
  'Delhi',
  'Faridabad',
  'Ghaziabad',
  'Gurugram',
  'Hyderabad',
  'Indore',
  'Jaipur',
  'Kanpur',
  'Kolkata',
  'Lucknow',
  'Ludhiana',
  'Mumbai',
  'Nagpur',
  'Noida',
  'Patna',
  'Pune',
  'Surat',
  'Thane',
  'Vadodara',
];

const DispositionModal = ({ open, onClose, call, onSave }) => {
  const [serviceType, setServiceType] = useState('');
  const [disposition, setDisposition] = useState('');
  const [price, setPrice] = useState('');
  const [city, setCity] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [prefilledFromHistory, setPrefilledFromHistory] = useState(false);
  const [citySuggestions, setCitySuggestions] = useState(DEFAULT_CITIES);
  const [isCityOpen, setIsCityOpen] = useState(false);

  const cleanPhone = (phone) => {
    if (!phone) return '';
    const digits = String(phone).replace(/\D/g, '');
    return digits;
  };

  const last10 = (phone) => {
    const digits = cleanPhone(phone);
    if (!digits) return '';
    return digits.slice(-10);
  };

  // Load existing disposition when modal opens
  useEffect(() => {
    const run = async () => {
      if (!open || !call) return;

      // Reset UI state per open
      setError(null);
      setSuccess(false);
      setPrefilledFromHistory(false);

      // Prefer V2 columns; fallback to legacy if present
      const initialServiceType = call.service_type || call.service_requests?.service_type || '';
      const initialDisposition = call.disposition || call.disposition_category || '';
      const initialPrice =
        call.price !== null && call.price !== undefined && call.price !== '' ? String(call.price) : '';
      const initialNotes = call.notes_detail || call.disposition_note || '';

      setServiceType(initialServiceType);
      setDisposition(initialDisposition);
      setPrice(initialPrice);
      setCity(call.city || '');
      setNotes(initialNotes);

      // If this call already has any disposition data, don't override from history
      const hasAnyCurrentDisposition =
        Boolean(initialServiceType) ||
        Boolean(initialDisposition) ||
        Boolean(initialNotes) ||
        Boolean(initialPrice);

      if (hasAnyCurrentDisposition) return;

      // Auto-prefill from last saved disposition for same customer number
      const phoneKey = last10(call.cnumber);
      if (!phoneKey) return;

      try {
        setPrefillLoading(true);
        const { data, error: fetchErr } = await supabase
          .from('call_reports')
          .select(
            'callid,cnumber,service_type,disposition,price,city,notes_detail,disposition_category,disposition_note,disposition_updated_at,createdat'
          )
          .ilike('cnumber', `%${phoneKey}`)
          .order('disposition_updated_at', { ascending: false, nullsFirst: false })
          .order('createdat', { ascending: false })
          .limit(1);

        if (fetchErr) throw fetchErr;
        const lastRow = data?.[0];
        if (!lastRow) return;

        const histServiceType = lastRow.service_type || '';
        const histDisposition = lastRow.disposition || lastRow.disposition_category || '';
        const histPrice =
          lastRow.price !== null && lastRow.price !== undefined && lastRow.price !== ''
            ? String(lastRow.price)
            : '';
        const histCity = lastRow.city || '';
        const histNotes = lastRow.notes_detail || lastRow.disposition_note || '';

        // Only set if we got something meaningful
        const hasAnyHistory =
          Boolean(histServiceType) ||
          Boolean(histDisposition) ||
          Boolean(histNotes) ||
          Boolean(histPrice) ||
          Boolean(histCity);
        if (!hasAnyHistory) return;

        setServiceType(histServiceType);
        setDisposition(histDisposition);
        setPrice(histPrice);
        setCity(histCity);
        setNotes(histNotes);
        setPrefilledFromHistory(true);
      } catch (e) {
        console.warn('⚠️ Could not prefill from history:', e?.message || e);
      } finally {
        setPrefillLoading(false);
      }
    };

    run();
  }, [open, call]);

  // Load city suggestions once (best-effort)
  useEffect(() => {
    const loadCities = async () => {
      if (!open) return;
      try {
        const { data, error: fetchErr } = await supabase
          .from('call_reports')
          .select('city')
          .not('city', 'is', null)
          .limit(500);
        if (fetchErr) throw fetchErr;
        const fromDb = (data || []).map((r) => (r.city || '').trim()).filter(Boolean);
        const unique = Array.from(new Set([...DEFAULT_CITIES, ...fromDb])).sort((a, b) => a.localeCompare(b));
        setCitySuggestions(unique);
      } catch (e) {
        console.warn('⚠️ Could not load city suggestions:', e?.message || e);
      }
    };
    loadCities();
  }, [open]);

  const filteredCitySuggestions = React.useMemo(() => {
    const q = (city || '').trim().toLowerCase();
    if (!q) return citySuggestions.slice(0, 12);
    // Prefer prefix matches (de -> Delhi), then contains matches
    const prefix = citySuggestions.filter((c) => c.toLowerCase().startsWith(q));
    const contains = citySuggestions.filter(
      (c) => !c.toLowerCase().startsWith(q) && c.toLowerCase().includes(q)
    );
    return [...prefix, ...contains].slice(0, 12);
  }, [city, citySuggestions]);

  const handleSave = async () => {
    if (!disposition) {
      setError('Please select a disposition');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('💾 Saving disposition for callid:', call.callid);

      const parsedPrice =
        price === '' || price === null || price === undefined ? null : Number(price);
      if (parsedPrice !== null && Number.isNaN(parsedPrice)) {
        setError('Price must be a number');
        setLoading(false);
        return;
      }

      const { data, error: updateError } = await supabase
        .from('call_reports')
        .update({
          // V2 columns
          service_type: serviceType || null,
          disposition: disposition || null,
          price: parsedPrice,
          city: city.trim() || null,
          notes_detail: notes.trim() || null,
          // Legacy columns (keep in sync for older UI areas)
          disposition_category: disposition || null,
          disposition_note: notes.trim() || null,
          disposition_updated_at: new Date().toISOString(),
          // disposition_updated_by: user.id, // Add if you have user context
        })
        .eq('callid', call.callid)
        .select();

      if (updateError) {
        console.error('❌ Error saving disposition:', updateError);
        throw updateError;
      }

      console.log('✅ Disposition saved:', data);
      setSuccess(true);

      // Call parent's onSave to refresh the list
      if (onSave) {
        onSave(data[0]);
      }

      // Close modal after a short delay
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (err) {
      console.error('💥 Error:', err);
      setError(err.message || 'Failed to save disposition');
    } finally {
      setLoading(false);
    }
  };

  const getLabel = (arr, value) => arr.find((x) => x.value === value)?.label || value;

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[95vh] md:max-h-[90vh] overflow-hidden">
        {/* Header - Mobile Responsive */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 md:px-6 py-3 md:py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2 min-w-0 flex-1">
            <ClipboardList size={20} className="flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <h2 className="text-base md:text-xl font-bold truncate">Call Disposition</h2>
              <p className="text-xs md:text-sm opacity-90 truncate">Call ID: {call?.callid || 'N/A'}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors flex-shrink-0 ml-2"
            disabled={loading}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content - Mobile Responsive */}
        <div className="p-4 md:p-6 overflow-y-auto max-h-[calc(95vh-180px)] md:max-h-[calc(90vh-180px)]">
          {/* Call Info */}
          <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="font-semibold text-gray-700">Customer:</span>
                <p className="text-gray-600">{call?.cnumber || 'N/A'}</p>
              </div>
              <div>
                <span className="font-semibold text-gray-700">Status:</span>
                {call?.service_request_id ? (
                  <span className="inline-flex items-center space-x-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                    <CheckCircle size={12} />
                    <span>Linked to Complaint</span>
                  </span>
                ) : (
                  <span className="inline-flex items-center space-x-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">
                    <AlertCircle size={12} />
                    <span>Not Linked</span>
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
              <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-red-800">Error</h4>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* Success Alert */}
          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start space-x-2">
              <CheckCircle size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-green-800">Success!</h4>
                <p className="text-sm text-green-700">Disposition saved successfully. Closing...</p>
              </div>
            </div>
          )}

          {/* Prefill info */}
          {prefillLoading && (
            <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg flex items-start space-x-2">
              <Loader size={18} className="text-indigo-600 flex-shrink-0 mt-0.5 animate-spin" />
              <div>
                <h4 className="font-semibold text-indigo-800">Loading previous note…</h4>
                <p className="text-sm text-indigo-700">Auto-filling last disposition for this customer number.</p>
              </div>
            </div>
          )}
          {prefilledFromHistory && !prefillLoading && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start space-x-2">
              <CheckCircle size={18} className="text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-green-800">Previous note loaded</h4>
                <p className="text-sm text-green-700">Same customer number detected — fields auto-filled from last call.</p>
              </div>
            </div>
          )}

          {/* Service Type */}
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Service Type
            </label>
            <select
              value={serviceType}
              onChange={(e) => setServiceType(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              disabled={loading}
            >
              <option value="">Select service type...</option>
              {SERVICE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          {/* Disposition */}
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Disposition <span className="text-red-500">*</span>
            </label>
            <select
              value={disposition}
              onChange={(e) => setDisposition(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              disabled={loading}
            >
              <option value="">Select disposition...</option>
              {DISPOSITIONS.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>

          {/* Price */}
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Price
            </label>
            <input
              type="number"
              inputMode="decimal"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="e.g. 499"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              disabled={loading}
            />
          </div>

          {/* City (Autosuggest) */}
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              City
            </label>
            <div className="relative">
              <input
                type="text"
                value={city}
                onChange={(e) => {
                  setCity(e.target.value);
                  setIsCityOpen(true);
                }}
                onFocus={() => setIsCityOpen(true)}
                onBlur={() => {
                  // Delay close to allow click selection
                  setTimeout(() => setIsCityOpen(false), 150);
                }}
                placeholder="Type e.g. de → Delhi, mu → Mumbai, ah → Ahmedabad"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                disabled={loading}
                autoComplete="off"
              />

              {isCityOpen && filteredCitySuggestions.length > 0 && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-auto">
                  {filteredCitySuggestions.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className="w-full text-left px-4 py-2 text-sm hover:bg-indigo-50 focus:bg-indigo-50 focus:outline-none"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setCity(c);
                        setIsCityOpen(false);
                      }}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Auto-suggest works on your typing (case-insensitive). Cities are loaded from saved records + built-in list.
            </p>
          </div>

          {/* Notes / Detail */}
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Notes / Detail
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="Add any additional details, reasons, or follow-up actions..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all resize-none"
              disabled={loading}
            />
            <p className="text-xs text-gray-500 mt-1">
              Explain why call wasn't linked, cancellation reason, customer issue, etc.
            </p>
          </div>

          {/* Current Disposition Preview */}
          {(call?.disposition || call?.disposition_category || call?.notes_detail || call?.disposition_note || call?.price || call?.service_type) && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs font-semibold text-gray-600 mb-2">Current Disposition:</p>
              <div className="flex flex-wrap gap-2">
                {(call.service_type || call.service_requests?.service_type) && (
                  <span className="inline-block px-3 py-1 rounded text-xs font-medium border bg-white text-gray-700 border-gray-300">
                    {getLabel(SERVICE_TYPES, call.service_type || call.service_requests?.service_type)}
                  </span>
                )}
                {(call.disposition || call.disposition_category) && (
                  <span className="inline-block px-3 py-1 rounded text-xs font-medium border bg-indigo-100 text-indigo-700 border-indigo-300">
                    {getLabel(DISPOSITIONS, call.disposition || call.disposition_category)}
                  </span>
                )}
                {call.city && (
                  <span className="inline-block px-3 py-1 rounded text-xs font-medium border bg-yellow-100 text-yellow-800 border-yellow-200">
                    {call.city}
                  </span>
                )}
                {(call.price !== null && call.price !== undefined && call.price !== '') && (
                  <span className="inline-block px-3 py-1 rounded text-xs font-medium border bg-green-100 text-green-700 border-green-300">
                    ₹{call.price}
                  </span>
                )}
              </div>
              {(call?.notes_detail || call?.disposition_note) && (
                <p className="text-sm text-gray-600 mt-2 italic">
                  "{call.notes_detail || call.disposition_note}"
                </p>
              )}
              {call?.disposition_updated_at && (
                <p className="text-xs text-gray-500 mt-2">
                  Last updated: {new Date(call.disposition_updated_at).toLocaleString('en-IN', {
                    timeZone: 'Asia/Kolkata',
                    dateStyle: 'medium',
                    timeStyle: 'short'
                  })}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-5 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading || !disposition || success}
            className="px-5 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {loading ? (
              <>
                <Loader size={16} className="animate-spin" />
                <span>Saving...</span>
              </>
            ) : success ? (
              <>
                <CheckCircle size={16} />
                <span>Saved!</span>
              </>
            ) : (
              <>
                <ClipboardList size={16} />
                <span>Save Disposition</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DispositionModal;
